import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { singleSourceApi, CreateSingleSourceFormDto, UpdateSingleSourceFormDto, UploadSingleSourceDocumentDto } from '../../../api/singleSource';
import { notifications } from '@mantine/notifications';

async function axiosBlobErrorMessage(e: unknown): Promise<string> {
  const err = e as { response?: { data?: unknown }; message?: string };
  const data = err.response?.data;
  if (data instanceof Blob) {
    try {
      const j = JSON.parse(await data.text()) as { error?: string };
      if (typeof j.error === 'string') return j.error;
    } catch {
      /* ignore */
    }
  }
  if (data && typeof data === 'object' && data !== null && 'error' in data) {
    return String((data as { error: string }).error);
  }
  return err.message || 'Failed to download document';
}

function saveBlobAsFile(blob: Blob, filename: string) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.setTimeout(() => window.URL.revokeObjectURL(url), 1500);
}

export function hasSingleSourceUploadedDocument(doc?: { objectKey: string }): boolean {
  return Boolean(doc?.objectKey && !doc.objectKey.includes('/placeholder-'));
}

// Query keys
export const singleSourceKeys = {
  all: ['singleSource'] as const,
  lists: () => [...singleSourceKeys.all, 'list'] as const,
  list: (filters: Record<string, any>) => [...singleSourceKeys.lists(), { filters }] as const,
  details: () => [...singleSourceKeys.all, 'detail'] as const,
  detail: (id: string) => [...singleSourceKeys.details(), id] as const,
};

// List single source forms
export function useSingleSourceForms(params?: { status?: string; page?: number; limit?: number }) {
  return useQuery({
    queryKey: singleSourceKeys.list(params || {}),
    queryFn: async () => {
      const response = await singleSourceApi.listForms(params);
      return response.data;
    },
  });
}

// Get single source form by ID
export function useSingleSourceForm(id: string, options?: { refetchOnMount?: boolean }) {
  return useQuery({
    queryKey: singleSourceKeys.detail(id),
    queryFn: async () => {
      const response = await singleSourceApi.getFormById(id);
      return response.data;
    },
    enabled: !!id,
    refetchOnMount: options?.refetchOnMount ? 'always' : undefined,
  });
}

// Create single source form
export function useCreateSingleSourceForm() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateSingleSourceFormDto) => {
      const response = await singleSourceApi.createForm(data);
      return response.data;
    },
    onSuccess: (form) => {
      queryClient.invalidateQueries({ queryKey: singleSourceKeys.lists() });
      notifications.show({
        title: 'Success',
        message: 'Single source form saved as draft. Send it to Head of Procurement for approval from the detail page.',
        color: 'green',
      });
      return form;
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.response?.data?.error || 'Failed to create single source form',
        color: 'red',
      });
    },
  });
}

// Update single source form
export function useUpdateSingleSourceForm() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateSingleSourceFormDto }) => {
      const response = await singleSourceApi.updateForm(id, data);
      return response.data;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: singleSourceKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: singleSourceKeys.lists() });
      notifications.show({
        title: 'Success',
        message: 'Single source form updated successfully',
        color: 'green',
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.response?.data?.error || 'Failed to update single source form',
        color: 'red',
      });
    },
  });
}

// Delete single source form
export function useDeleteSingleSourceForm() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await singleSourceApi.deleteForm(id);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: singleSourceKeys.lists() });
      notifications.show({
        title: 'Success',
        message: 'Single source form deleted successfully',
        color: 'green',
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.response?.data?.error || 'Failed to delete single source form',
        color: 'red',
      });
    },
  });
}

// Submit single source form
export function useSubmitSingleSourceForm() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await singleSourceApi.submitForm(id);
      return response.data;
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: singleSourceKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: singleSourceKeys.lists() });
      queryClient.invalidateQueries({ queryKey: ['singleSource', 'pendingApprovals'] });
      notifications.show({
        title: 'Success',
        message: 'Single source form sent to Head of Procurement for approval',
        color: 'green',
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.response?.data?.error || 'Failed to submit single source form',
        color: 'red',
      });
    },
  });
}

// Upload single source form document
export function useUploadSingleSourceDocument() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UploadSingleSourceDocumentDto }) => {
      const response = await singleSourceApi.uploadDocument(id, data);
      return response.data;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: singleSourceKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: singleSourceKeys.lists() });
      notifications.show({
        title: 'Success',
        message: 'Document uploaded successfully',
        color: 'green',
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.response?.data?.error || 'Failed to upload document',
        color: 'red',
      });
    },
  });
}

// Download single source form document
export function useDownloadSingleSourceDocument() {
  return useMutation({
    mutationFn: async ({ id, fileName }: { id: string; fileName: string }) => {
      const response = await singleSourceApi.downloadDocument(id);
      const blob = response.data as Blob;
      if (blob.type.includes('json')) {
        const text = await blob.text();
        try {
          const j = JSON.parse(text) as { error?: string };
          throw new Error(j.error || 'Failed to download document');
        } catch (e) {
          if (e instanceof Error && e.message !== 'Failed to download document') throw e;
        }
      }
      return { blob, fileName };
    },
    onSuccess: ({ blob, fileName }) => {
      saveBlobAsFile(blob, fileName);
      notifications.show({
        title: 'Success',
        message: 'Document downloaded successfully',
        color: 'green',
      });
    },
    onError: async (error: unknown) => {
      notifications.show({
        title: 'Error',
        message: await axiosBlobErrorMessage(error),
        color: 'red',
      });
    },
  });
}

// Generate PDF for single source form
export function useGenerateSingleSourceFormPDF() {
  return useMutation({
    mutationFn: async (id: string) => {
      const response = await singleSourceApi.generatePDF(id);
      return response.data;
    },
    onSuccess: (blob, id) => {
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `single-source-form-${id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      notifications.show({
        title: 'Success',
        message: 'PDF generated and downloaded successfully',
        color: 'green',
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.response?.data?.error || 'Failed to generate PDF',
        color: 'red',
      });
    },
  });
}

// Approve single source form
export function useApproveSingleSourceForm() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, comments }: { id: string; comments?: string }) => {
      const response = await singleSourceApi.approveForm(id, comments ? { comments } : undefined);
      return response.data;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: singleSourceKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: singleSourceKeys.lists() });
      queryClient.invalidateQueries({ queryKey: ['singleSource', 'pendingApprovals'] });
      notifications.show({
        title: 'Success',
        message: 'Single source form approved successfully',
        color: 'green',
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.response?.data?.error || 'Failed to approve single source form',
        color: 'red',
      });
    },
  });
}

// Reject single source form
export function useRejectSingleSourceForm() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, comments }: { id: string; comments: string }) => {
      const response = await singleSourceApi.rejectForm(id, { comments });
      return response.data;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: singleSourceKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: singleSourceKeys.lists() });
      queryClient.invalidateQueries({ queryKey: ['singleSource', 'pendingApprovals'] });
      notifications.show({
        title: 'Success',
        message: 'Single source form rejected',
        color: 'orange',
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.response?.data?.error || 'Failed to reject single source form',
        color: 'red',
      });
    },
  });
}

// Get pending approvals
export function usePendingSingleSourceApprovals(enabled = true) {
  return useQuery({
    queryKey: ['singleSource', 'pendingApprovals'],
    enabled,
    queryFn: async () => {
      const response = await singleSourceApi.getPendingApprovals();
      return response.data;
    },
  });
}
